import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { FuseAlertModule } from '@fuse/components/alert';
import { KeycloakUser } from 'app/app.type';
import { Observable } from 'rxjs';
import { EditUserModalComponent } from './edit-user-modal/edit-user-modal.component';
import { RegisterUserModalComponent } from './register-user-modal/register-user-modal.component';
import { KeycloakAdminService } from './service/keycloak-admin.service';


@Component({
	selector: 'user-management',
	standalone: true,
	imports: [CommonModule, MatIconModule, FuseAlertModule, MatTableModule, MatChipsModule, MatButtonModule,
		MatMenuModule, MatDialogModule, MatPaginatorModule, MatFormFieldModule, MatInputModule, FormsModule,
		RouterModule, MatTooltipModule, MatSnackBarModule],
	templateUrl: './user-management.component.html'
})
export class UserManagementComponent implements OnInit, AfterViewInit {
	private readonly keycloakAdminService = inject(KeycloakAdminService);
	private readonly dialog = inject(MatDialog);
	private snackBar = inject(MatSnackBar);
	snackBarOptions = { duration: 3000 , panelClass: ['warning-snackbar']}

	@ViewChild(MatPaginator) paginator!: MatPaginator;

	users$: Observable<KeycloakUser[]>;
	users: KeycloakUser[] = [];
	dataSource = new MatTableDataSource<KeycloakUser>();
	displayedColumns: string[] = ['name', 'email', 'enabled', 'actions'];
	loading = false;
	error: string | null = null;
	hasPermissions = false;
	checkingPermissions = true;

	// Search properties
	searchTerm = '';

	// Pagination properties
	pageSize = 10;
	pageSizeOptions = [5, 10, 25, 50];
	totalUsers = 0;

	constructor() { }

	ngOnInit(): void {
		this.checkPermissions();
		this.setupSearchFilter();

		this.keycloakAdminService.getGroups().subscribe(res => console.warn(res))
	}

	setupSearchFilter(): void {
		// Configurar filtro customizado para pesquisa
		this.dataSource.filterPredicate = (data: KeycloakUser, filter: string) => {
			const searchText = filter.toLowerCase();
			const fullName = `${data.firstName || ''} ${data.lastName || ''}`.toLowerCase();
			const email = (data.email || '').toLowerCase();
			const status = data.enabled ? 'active' : 'inactive';

			return fullName.includes(searchText) ||
				   email.includes(searchText) ||
				   status.includes(searchText);
		};
	}

	applyFilter(): void {
		this.dataSource.filter = this.searchTerm.trim();

		// Reset pagination to first page when filtering
		if (this.dataSource.paginator) {
			this.dataSource.paginator.firstPage();
		}
	}

	get filteredUsersCount(): number {
		return this.dataSource.filteredData?.length || 0;
	}

	ngAfterViewInit(): void {
		// Configurar paginator com delay para garantir que a view está completamente inicializada
		setTimeout(() => {
			if (this.paginator) {
				this.paginator.pageSize = this.pageSize;
				this.dataSource.paginator = this.paginator;
			}
		}, 0);
	}

	checkPermissions(): void {
		this.checkingPermissions = true;

		this.keycloakAdminService.hasAdminPermissions().subscribe({
			next: (hasPermissions) => {
				this.hasPermissions = hasPermissions;
				this.checkingPermissions = false;

				if (hasPermissions) {
					this.loadUsers();
				} else {
					this.error = 'Usuário não possui permissões administrativas para acessar a lista de usuários do Keycloak';
				}
			},
			error: (error) => {
				this.checkingPermissions = false;
				this.hasPermissions = false;
				console.error('Erro ao verificar permissões:', error);
				this.snackBar.open(error, 'Fechar', this.snackBarOptions);
			}
		});
	}

	loadUsers(): void {
		this.loading = true;
		this.error = null;

		// Carregar todos os usuários para paginação no frontend
		this.users$ = this.keycloakAdminService.getAllUsers();

		this.users$.subscribe({
			next: (users) => {
				this.users = users;
				this.dataSource.data = users;
				this.totalUsers = users.length;

				// Garantir que o paginator está conectado após o carregamento dos dados
				setTimeout(() => {
					if (this.paginator) {
						this.dataSource.paginator = this.paginator;
						this.paginator.pageSize = this.pageSize;
					}
				}, 0);

				this.loading = false;
			},
			error: (error) => {
				this.loading = false;
				console.error('Erro ao buscar usuários:', error);
				this.snackBar.open(error, 'Fechar', this.snackBarOptions);
			}
		});
	}

	editUser(user: KeycloakUser): void {
		const dialogRef = this.dialog.open(EditUserModalComponent, {
			data: { user: user }
		});

		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				console.log('Usuário atualizado:', result);

				// Implementar chamada para API do Keycloak para atualizar usuário
				this.keycloakAdminService.updateUser(result.id, {
					username: result.username,
					firstName: result.firstName,
					lastName: result.lastName,
					email: result.email
				}).subscribe({
					next: () => {
						// Recarregar todos os usuários
						this.loadUsers();
						console.log('User updated successfully');
					},
					error: (error) => {
						console.error('Error updating user:', error);
						this.snackBar.open(error, 'Close', this.snackBarOptions);
					}
				});
			}
		});
	}

	registerUser(): void {
		const dialogRef = this.dialog.open(RegisterUserModalComponent);

		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				console.log('Usuário registrado:', result);
				// Recarregar a lista de usuários
				this.loadUsers();
				this.snackBar.open('User registered successfully!', 'Close', {
					duration: 3000,
					panelClass: ['success-snackbar']
				});
			}
		});
	}

	toggleUserStatus(user: KeycloakUser): void {
		const body = { enabled: !user.enabled };

		this.keycloakAdminService.updateUser(user.id, body).subscribe({
			next: () => {
				this.loadUsers(); // Recarregar lista completa
			},
			error: (error) => {
				console.error('Error updating user:', error);
				this.snackBar.open(error, 'Close', this.snackBarOptions);
			}
		});
	}
}
